import { CalendarCheck } from "lucide-react"
import { useEffect, useRef } from "react"

// IMP-395: the provider's inline booking calendar, rendered as a message-level
// card in the website-chat stream when the agent reaches its booking step.
// Two providers, two embed strategies:
//   - Calendly: a plain iframe of the public scheduling URL. Calendly's
//     embedded page posts lifecycle events (calendly.event_scheduled) to its
//     parent window — no script needed.
//   - Cal.com: their official embed loader (queue shim + embed.js), which
//     renders into a container and emits "bookingSuccessful" through its API.
// Completion payloads are read defensively — both providers' event shapes are
// outside our control, and a missing field must never lose the booking (the
// server tolerates blank fields).

export interface BookingPrefill {
  name?: string
  email?: string
}

export interface BookingCompletion {
  external_ref: string
  invitee_name: string
  invitee_email: string
  start_time_iso8601: string
}

interface CalApi {
  (...args: unknown[]): void
  q?: unknown[]
  ns?: Record<string, unknown>
  loaded?: boolean
}

declare global {
  interface Window {
    Cal?: CalApi
  }
}

// Cal.com's official embed loader snippet, ported: installs a command queue
// on window.Cal and injects embed.js once; queued calls flush when it loads.
function loadCal(): CalApi {
  if (window.Cal) return window.Cal

  const cal: CalApi = function (...args: unknown[]) {
    if (!cal.loaded) {
      cal.ns = {}
      cal.q = cal.q ?? []
      const script = document.createElement("script")
      script.src = "https://app.cal.com/embed/embed.js"
      document.head.appendChild(script)
      cal.loaded = true
    }
    cal.q!.push(args)
  }
  cal.q = []
  window.Cal = cal
  return cal
}

// "https://cal.com/acme/intro-call" → "acme/intro-call" (the embed's calLink).
function calLinkFrom(url: string): string | null {
  try {
    return new URL(url).pathname.replace(/^\/+/, "") || null
  } catch {
    return null
  }
}

function calendlyIframeSrc(
  url: string,
  color: string,
  prefill: BookingPrefill,
): string {
  try {
    const src = new URL(url)
    src.searchParams.set("embed_domain", window.location.hostname)
    src.searchParams.set("embed_type", "Inline")
    src.searchParams.set("hide_gdpr_banner", "1")
    src.searchParams.set("primary_color", color.replace(/^#/, ""))
    if (prefill.name) src.searchParams.set("name", prefill.name)
    if (prefill.email) src.searchParams.set("email", prefill.email)
    return src.toString()
  } catch {
    return url
  }
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function dig(obj: unknown, ...keys: string[]): unknown {
  let current: unknown = obj
  for (const key of keys) {
    if (typeof current !== "object" || current === null) return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function CalendlyEmbed({
  url,
  color,
  prefill,
  onBooked,
}: {
  url: string
  color: string
  prefill: BookingPrefill
  onBooked: (completion: BookingCompletion) => void
}) {
  const bookedRef = useRef(false)

  useEffect(() => {
    function handle(e: MessageEvent) {
      if (!/https:\/\/([a-z0-9-]+\.)?calendly\.com$/.test(e.origin)) return
      const event = dig(e.data, "event")
      if (event !== "calendly.event_scheduled" || bookedRef.current) return

      bookedRef.current = true
      onBooked({
        external_ref: asString(dig(e.data, "payload", "event", "uri")),
        invitee_name: prefill.name ?? "",
        invitee_email: prefill.email ?? "",
        start_time_iso8601: asString(
          dig(e.data, "payload", "event", "start_time"),
        ),
      })
    }
    window.addEventListener("message", handle)
    return () => window.removeEventListener("message", handle)
  }, [onBooked, prefill.name, prefill.email])

  return (
    <iframe
      src={calendlyIframeSrc(url, color, prefill)}
      title="Book an appointment"
      className="h-full w-full border-0"
    />
  )
}

function CalComEmbed({
  url,
  color,
  prefill,
  onBooked,
}: {
  url: string
  color: string
  prefill: BookingPrefill
  onBooked: (completion: BookingCompletion) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const bookedRef = useRef(false)

  useEffect(() => {
    const el = containerRef.current
    const calLink = calLinkFrom(url)
    if (!el || !calLink) return

    const cal = loadCal()
    cal("init", { origin: "https://cal.com" })
    cal("inline", {
      elementOrSelector: el,
      calLink,
      config: {
        ...(prefill.name && { name: prefill.name }),
        ...(prefill.email && { email: prefill.email }),
      },
    })
    cal("ui", {
      hideEventTypeDetails: false,
      cssVarsPerTheme: {
        light: { "cal-brand": color },
        dark: { "cal-brand": color },
      },
    })
    cal("on", {
      action: "bookingSuccessful",
      callback: (e: unknown) => {
        if (bookedRef.current) return
        bookedRef.current = true
        const data = dig(e, "detail", "data")
        onBooked({
          external_ref: asString(dig(data, "booking", "uid")),
          invitee_name:
            asString(dig(data, "booking", "attendees", "0", "name")) ||
            (prefill.name ?? ""),
          invitee_email:
            asString(dig(data, "booking", "attendees", "0", "email")) ||
            (prefill.email ?? ""),
          start_time_iso8601:
            asString(dig(data, "date")) ||
            asString(dig(data, "booking", "startTime")),
        })
      },
    })
    // Cal.com's embed API has no teardown; clearing the container on unmount
    // is the documented-enough cleanup for an inline embed.
    return () => {
      el.innerHTML = ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  return <div ref={containerRef} className="h-full w-full overflow-y-auto" />
}

export function BookingCalendarCard({
  provider,
  url,
  prefill,
  status,
  color,
  onBooked,
}: {
  provider: "calendly" | "cal_com"
  url: string
  prefill: BookingPrefill
  status: "open" | "booked"
  color: string
  onBooked: (completion: BookingCompletion) => void
}) {
  if (status === "booked") {
    return (
      <div className="flex max-w-[85%] items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm shadow-sm">
        <CalendarCheck className="size-4 shrink-0" style={{ color }} />
        <span className="font-medium text-zinc-900">Appointment booked</span>
      </div>
    )
  }

  return (
    <div className="h-[480px] w-full overflow-hidden rounded-2xl border bg-white shadow-sm">
      {provider === "calendly" ? (
        <CalendlyEmbed
          url={url}
          color={color}
          prefill={prefill}
          onBooked={onBooked}
        />
      ) : (
        <CalComEmbed
          url={url}
          color={color}
          prefill={prefill}
          onBooked={onBooked}
        />
      )}
    </div>
  )
}
